import { ActionMenu, type ActionMenuItem } from "@/components/ActionMenu";
import type { Bill } from "@/lib/api";
import { billBalanceCents } from "@/lib/worklist";

/** Row actions for one bill. Payment stays on the list; credit, recurring, and scheduling open inside their consolidated views. */
export function BillRowActions({
  bill,
  acting,
  onRecognize,
  onPay,
}: {
  bill: Bill;
  acting: boolean;
  onRecognize: (bill: Bill) => void;
  onPay: (billId: string) => void;
}) {
  const status = bill.status.toLowerCase();
  const canRecognize = status === "draft";
  const canPay =
    billBalanceCents(bill) > 0 &&
    (status === "received" || status === "overdue");

  const items: ActionMenuItem[] = [
    {
      id: "open",
      label: "Open bill",
      to: "/bills/$billId",
      params: { billId: bill.id },
    },
    ...(canRecognize
      ? [
          {
            id: "recognize",
            label: "Recognize",
            title: bill.issue_date
              ? `Posts the expense and payables journal on ${bill.issue_date}`
              : "Add an issue date before recognizing this bill",
            disabled: acting || !bill.issue_date,
            busy: acting,
            busyLabel: "Recognizing…",
            onSelect: () => onRecognize(bill),
          },
        ]
      : []),
    ...(canPay
      ? [
          {
            id: "pay",
            label: "Pay",
            title: "Record a payment against this bill (bookkeeping only)",
            onSelect: () => onPay(bill.id),
          },
        ]
      : []),
    {
      id: "supplier-credit",
      label: "Supplier credit",
      to: "/bills",
      search: (previous: Record<string, unknown>) => ({
        ...previous,
        view: "credits",
        bill: bill.id,
      }),
    },
    {
      id: "make-recurring",
      label: "Make recurring",
      to: "/bills",
      search: (previous: Record<string, unknown>) => ({
        ...previous,
        view: "recurring",
      }),
    },
    ...(canPay
      ? [
          {
            id: "schedule-payment",
            label: "Schedule payment",
            to: "/payments",
            search: (previous: Record<string, unknown>) => ({
              ...previous,
              tab: "scheduled",
              bill: bill.id,
            }),
          },
        ]
      : []),
  ];

  return (
    <ActionMenu
      label="Actions"
      ariaLabel={`Actions for ${bill.bill_number}`}
      items={items}
    />
  );
}
